/**
 * AuthEngineResponse
 */

import { MetadataKey, MetadataArgsMap, MetadataValue } from './engine/AuthEngineTypes'
import { Provider } from './services/config/MVPDConfigAPI'

/** @hidden */
export interface BaseEventResult<E> {
  type: E

  [x: string]: any
}

export type EventResult<E> = BaseEventResult<E>

/** @hidden */
export enum AuthEngineResponseType {
  Did_Set_Requestor_Complete = 'didSetRequestorComplete',
  Did_Set_Authentication_Status = 'didSetAuthenticationStatus',
  Did_Receive_Selected_Provider = 'didReceiveSelectedProvider',
  Did_Set_Token = 'didSetToken',
  Did_Receive_Preauthorized_Resources = 'didReceivePreauthorizedResources',
  Did_Set_Metadata_Status = 'didSetMetadataStatus',
}

/** @hidden */
export interface BaseAuthEngineResponse extends EventResult<AuthEngineResponseType> { }

/**
 * Auth Engine Set Requestor Response
 *
 * The value returned from successful calls to `IAuthEngine.setRequestor`
 *
 * @hidden
 */
export interface SetRequestorResponse extends BaseAuthEngineResponse {
  readonly status: boolean
}

/**
 * Authentication Status Response
 *
 * The value returned from successful calls to:
 *   - `fetchAuthContext()`
 *   - `login()`
 *   - `logout()`
 *   - `handleRedirectURL()`
 */
export interface AuthStatusResponse extends BaseAuthEngineResponse {
  readonly isAuthenticated: boolean
  readonly info: string
  readonly shouldLogoutInSettings: boolean
}

/**
 * Auth Engine Get Selected Provider Response
 *
 * The value returned from successful calls to `IAuthEngine.getSelectedProvider`
 *
 * @hidden
 */
export interface GetSelectedProviderResponse extends BaseAuthEngineResponse {
  readonly provider?: Provider
}

/**
 * Auth Engine Authorization Data Response
 *
 * The value returned from successful calls to `IAuthEngine.authorize`
 *
 * @hidden
 */
export interface AuthorizationDataResponse extends BaseAuthEngineResponse {
  readonly channel: string
  readonly token: string
}

/**
 * A preauthorization result for an individual resource. This can only be
 * obtained in the PreauthorizationDataResponse return from IAuthEngine.preauthorize.
 */
export interface PreauthorizationDecision {
  readonly id: string
  readonly authorized: boolean
  readonly error?: any
}

/**
 * Auth Engine Preauthorize Data Response
 *
 * The value returned from successful calls to `IAuthEngine.preauthorize`
 *
 * @hidden
 */
export interface PreauthorizationDataResponse extends BaseAuthEngineResponse {
  readonly decisions: PreauthorizationDecision[]
}

/**
 * Auth Engine Metadata Response
 *
 * The value returned from successful calls to `IAuthEngine.fetchMetadata`
 *
 * @hidden
 */
export interface MetadataDataResponse extends BaseAuthEngineResponse {
  readonly key: MetadataKey
  args?: MetadataArgsMap
  encrypted: boolean
  readonly value: MetadataValue
  resource?: string
}

export type AuthEngineResponse =
  | SetRequestorResponse
  | AuthStatusResponse
  | AuthorizationDataResponse
  | PreauthorizationDataResponse
  | GetSelectedProviderResponse
  | MetadataDataResponse
